import { Fragment, useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth }           from '@/contexts/AuthContext';
import FilterBar             from '@/components/FilterBar';
import type { FilterField }  from '@/components/FilterBar';
import PaginationBar         from '@/components/PaginationBar';
import SortableTh            from '@/components/SortableTh';
import ExportButton          from '@/components/ExportButton';
import type { ExportScope }  from '@/components/ExportButton';
import { useSort }           from '@/hooks/useSort';
import { exportData, exportFmtDate } from '@/utils/export';
import type { ExportFormat } from '@/utils/export';
import { formatDateDMY } from '@/utils/date';
import { listFamilies, getChurchPlaceholderFamily } from '@/api/family';
import type { FamilyListItem } from '@/api/family';
import { createFamilyMember, updateFamilyMember, listFamilyMembers } from '@/api/familyMember';
import type { FamilyMemberPayload } from '@/api/familyMember';
import {
  listCemeteries, listEligibleMembers, saveCemeteryRecord,
  getCemeterySubscription, setCemeterySubscription, deleteCemeteryRecord,
} from '@/api/cemetery';
import type {
  CemeteryRecord, EligibleMember,
} from '@/api/cemetery';

// Same relationship options as the Family Member form, so an ad-hoc "Other"
// entry here stays consistent with how relationships are recorded elsewhere.
const OTHER_RELATIONSHIPS = [
  'Father', 'Mother', 'Grandfather', 'Grandmother',
  'Father-in-law', 'Mother-in-law', 'Brother', 'Sister',
  'Brother-in-law', 'Sister-in-law', 'Uncle', 'Aunt', 'Other',
];
const OTHER_GENDERS = ['Male', 'Female'];

const pickStr = (row: Record<string, unknown> | undefined, keys: string[]): string => {
  if (!row) return '';
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null) return String(v);
  }
  return '';
};
const pickBool = (row: Record<string, unknown> | undefined, keys: string[]): boolean => {
  if (!row) return false;
  for (const k of keys) {
    const v = row[k];
    if (v !== undefined && v !== null) return v === true || v === 1 || v === '1' || v === 'true';
  }
  return false;
};
const pickDate = (row: Record<string, unknown> | undefined, keys: string[]): string | null => {
  const v = pickStr(row, keys);
  return v ? v.slice(0, 10) : null;
};

// sp_SaveFamilyMember's UPDATE branch overwrites every column with whatever
// is sent — there's no partial-patch semantics. So changing just the gender
// from this page means re-sending every other field exactly as it already
// is, read from the member's full record, or the update would silently
// blank out their occupation, phone, ministries, etc.
const buildMemberPayloadPreserving = (
  existing: Record<string, unknown> | undefined,
  overrides: { gender: string },
): FamilyMemberPayload => ({
  name:               pickStr(existing, ['name', 'Name']),
  relationship:       pickStr(existing, ['relationship', 'Relationship']) || 'Other',
  gender:             overrides.gender,
  dob:                pickDate(existing, ['dob', 'DOB']),
  status:             pickStr(existing, ['member_status', 'status', 'Status']) || 'Active',
  phone:              pickStr(existing, ['phone', 'Phone']),
  email:              pickStr(existing, ['email', 'Email']),
  occupation:         pickStr(existing, ['occupation', 'Occupation']),
  qualification:      pickStr(existing, ['qualification', 'Qualification']),
  bloodGroup:         pickStr(existing, ['bloodGroup', 'BloodGroup', 'blood_group']),
  baptizedDate:       pickDate(existing, ['baptismDate', 'BaptismDate']),
  marriageDate:       pickDate(existing, ['marriageDate', 'MarriageDate']),
  confirmationDate:   pickDate(existing, ['confirmationDate', 'confirmation']),
  firstCommunionDate: pickDate(existing, ['communionDate', 'Communion']),
  priesthoodDate:     pickDate(existing, ['priesthoodDate', 'Priesthood']),
  memberGroup:        pickStr(existing, ['memberGroup', 'MemberGroup']),
  childClass:         pickStr(existing, ['childClass', 'ChildClass']),
  childInstitution:   pickStr(existing, ['childInstitution', 'ChildInstitution']),
  isAdmin:            pickBool(existing, ['isAdmin', 'isadmin']),
  legionOfMary:       pickBool(existing, ['isLOM', 'legionofmary']),
  isVincentDePaul:    pickBool(existing, ['isVDPaul', 'isvencentdepaul']),
  isChoir:            pickBool(existing, ['isChoir', 'ischoir']),
  isCatechismTeacher: pickBool(existing, ['isCatechismTeacher', 'iscatechismteacher']),
  isWomenAssoc:       pickBool(existing, ['isWomenAssoc', 'iswomenassoc']),
  isLiturgyCouncil:   pickBool(existing, ['isLitergyCouncil', 'islitergycouncil']),
  isAlterServices:    pickBool(existing, ['isAlterServices', 'isalterservices']),
  isYouth:            pickBool(existing, ['isYouth', 'isyouth']),
});

const currentYear = new Date().getFullYear();
// Same range as the main Subscriptions page (2023 onward) so historical
// cemetery dues can be recorded, not just the current/adjacent years.
const SUB_START_YEAR = 2023;
const YEAR_OPTIONS = Array.from(
  { length: currentYear - SUB_START_YEAR + 2 },
  (_, i) => SUB_START_YEAR + i,
);

const FILTER_FIELDS: FilterField[] = [
  { key: 'search',     label: 'Search', type: 'text', placeholder: 'Name or grave number…' },
  { key: 'linked',     label: 'Family', type: 'select',
    options: [{ label: 'Linked to a family', value: 'true' }, { label: 'Not linked', value: 'false' }] },
  { key: 'burialFrom', label: 'Burial from', type: 'date' },
  { key: 'burialTo',   label: 'Burial to',   type: 'date' },
];

const DEFAULT_FILTERS = {
  search:     null as null | string,
  linked:     null as null | string,
  burialFrom: null as null | string,
  burialTo:   null as null | string,
};

/** The list-query params shared by the grid and its export. */
const filterParams = (f: typeof DEFAULT_FILTERS) => ({
  search:     f.search ?? undefined,
  linked:     f.linked === 'true' ? true : f.linked === 'false' ? false : undefined,
  burialFrom: f.burialFrom ?? undefined,
  burialTo:   f.burialTo ?? undefined,
});

const fmtDate = formatDateDMY;

// The placeholder family's own code ('CHURCH') is an internal bookkeeping
// detail — show "Not linked" instead so it doesn't look like a real family.
const familyCodeLabel = (code?: string | null): string =>
  code === 'CHURCH' ? 'Not linked' : (code ?? '—');

// Church (unlinked) records can be saved with no formal member — deceased_name
// is then blank and whoever's buried there is only described in Notes.
// Fall back to showing that instead of an empty Name cell.
const displayName = (row: { name?: string | null; notes?: string | null }): string =>
  row.name?.trim() || row.notes?.trim() || '—';

// This year's cemetery subscription is stored per FAMILY, not per grave —
// so this badge is the same for every row sharing a family_id, making it
// visible at a glance that paying under one already covers the rest.
const SubStatusBadge = ({ status }: { status?: string | null }) => {
  const s = status ?? '';
  const cls = s === 'Paid'
    ? 'bg-green-100 text-green-800'
    : s === 'Pending'
    ? 'bg-amber-100 text-amber-800'
    : 'bg-gray-100 text-gray-600';
  return (
    <span className={`text-xs px-1.5 py-0.5 rounded font-georgia ${cls}`}>
      {s || '—'}
    </span>
  );
};

export default function CemeteryPage() {
  const { isAdmin } = useAuth();

  // Add/Edit-record panel
  const [showAdd,      setShowAdd]      = useState(false);
  const [editId,       setEditId]       = useState<number | null>(null);
  const [editMember,   setEditMember]   = useState<{ memberId: number; name: string } | null>(null);
  // Full member record + editable gender, loaded when opening an existing
  // cemetery record so gender can be corrected here without a trip to the
  // Family page — see buildMemberPayloadPreserving for why the full record
  // is needed rather than just the gender value.
  const [editMemberFull,   setEditMemberFull]   = useState<Record<string, unknown> | undefined>(undefined);
  const [editMemberGender, setEditMemberGender] = useState('');
  // The family this record started on when Edit was opened — used only to
  // decide whether "Change family" (linking to a real family) is offered:
  // only for records still on the CHURCH placeholder, never for a record
  // that already belongs to a real family (accidental re-homing guard).
  const [editOriginalFamilyCode, setEditOriginalFamilyCode] = useState('');
  const [famSearch,    setFamSearch]    = useState('');
  const [famResults,   setFamResults]   = useState<FamilyListItem[]>([]);
  const [famSearching, setFamSearching] = useState(false);
  // "Not linked to a family yet" — attaches to the placeholder church family
  // instead of a searched one, using the exact same "Other" ad-hoc-member
  // flow already built for known families.
  const [placeholderLoading, setPlaceholderLoading] = useState(false);
  const [selFamily,    setSelFamily]    = useState<FamilyListItem | null>(null);
  const [eligible,     setEligible]     = useState<EligibleMember[]>([]);
  // New records can add several family members who share one physical grave
  // in a single submission (e.g. spouse + grandparent buried together) —
  // one cemetery_details row gets created per selected member, all sharing
  // the same grave number/burial details. Editing an existing record still
  // targets exactly the one member it already belongs to.
  const [selMemberIds, setSelMemberIds] = useState<number[]>([]);
  // For people already buried before this system existed and never entered
  // as a family member — lets the admin add them by name/relationship
  // instead of blocking the cemetery record on the family list being complete.
  const [addOther,         setAddOther]         = useState(false);
  const [otherName,        setOtherName]        = useState('');
  const [otherRelationship, setOtherRelationship] = useState(OTHER_RELATIONSHIPS[0]);
  const [otherGender,      setOtherGender]      = useState('');
  const [graveNumber,  setGraveNumber]  = useState('');
  const [burialPlace,  setBurialPlace]  = useState('');
  const [dateOfDeath,  setDateOfDeath]  = useState('');
  const [dateOfBurial, setDateOfBurial] = useState('');
  const [notes,        setNotes]        = useState('');
  const [saving,       setSaving]       = useState(false);
  const [saveErr,      setSaveErr]      = useState('');

  // Subscription quick-edit (expand row)
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [subYear,    setSubYear]    = useState(currentYear);
  const [subLoading, setSubLoading] = useState(false);
  const [subAmount,  setSubAmount]  = useState('');
  const [subDate,    setSubDate]    = useState('');
  const [subStatus,  setSubStatus]  = useState<'Paid' | 'Unpaid' | 'Pending'>('Pending');
  const [subSaving,  setSubSaving]  = useState(false);
  const [subErr,     setSubErr]     = useState('');

  // Delete confirmation
  const [deleteTarget, setDeleteTarget] = useState<CemeteryRecord | null>(null);
  const [deleteErr,    setDeleteErr]    = useState('');
  const [deleting,     setDeleting]     = useState(false);

  const [rows,       setRows]       = useState<CemeteryRecord[]>([]);
  const [total,      setTotal]      = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading,    setLoading]    = useState(false);
  const [fetchErr,   setFetchErr]   = useState('');

  const [page,     setPage]     = useState(1);
  const [pageSize, setPageSize] = useState(25);
  // ?linked=false deep-link from the Dashboard's Graves Not Linked card
  const [filters,  setFilters]  = useState(() => {
    const l = new URLSearchParams(window.location.search).get('linked');
    return { ...DEFAULT_FILTERS, linked: l === 'true' || l === 'false' ? l : null };
  });

  // Deep-link from the Family detail page: /cemetery?familyId=X&familyCode=DE16
  // narrows the grid to that one family's graves until "Show all" is clicked.
  const [searchParams, setSearchParams] = useSearchParams();
  const familyIdFilter   = parseInt(searchParams.get('familyId') ?? '', 10) || null;
  const familyCodeFilter = searchParams.get('familyCode');
  const clearFamilyFilter = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('familyId');
      next.delete('familyCode');
      return next;
    });
    setPage(1);
  };

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchErr('');
    try {
      const result = await listCemeteries({
        page, pageSize, ...filterParams(filters),
        familyId: familyIdFilter ?? undefined,
      });
      setRows(result.items);
      setTotal(result.total);
      setTotalPages(result.totalPages);
    } catch {
      setFetchErr('Failed to load cemetery records. Please refresh.');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters, familyIdFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // ── Add-record panel logic ──────────────────────────────────
  const runFamSearch = useCallback(async () => {
    if (!famSearch.trim()) { setFamResults([]); return; }
    setFamSearching(true);
    try {
      const result = await listFamilies({ page: 1, pageSize: 10, head: famSearch.trim() });
      setFamResults(result.items);
    } catch {
      setFamResults([]);
    } finally {
      setFamSearching(false);
    }
  }, [famSearch]);

  useEffect(() => {
    const t = setTimeout(runFamSearch, 350);
    return () => clearTimeout(t);
  }, [runFamSearch]);

  const selectFamilyForAdd = async (f: FamilyListItem) => {
    setSelFamily(f);
    setFamResults([]);
    setFamSearch('');
    setSelMemberIds([]);
    setAddOther(false); setOtherName(''); setOtherRelationship(OTHER_RELATIONSHIPS[0]); setOtherGender('');
    try {
      const members = await listEligibleMembers(f.familyId);
      setEligible(members);
    } catch {
      setEligible([]);
    }
  };

  const selectChurchPlaceholder = async () => {
    setPlaceholderLoading(true);
    setSaveErr('');
    try {
      const ph = await getChurchPlaceholderFamily();
      await selectFamilyForAdd({
        familyId: ph.familyId, familyCode: ph.familyCode, familyHead: ph.familyHead,
      } as FamilyListItem);
    } catch {
      setSaveErr('Failed to load the placeholder family. Try again.');
    } finally {
      setPlaceholderLoading(false);
    }
  };

  const toggleSelMember = (memberId: number) =>
    setSelMemberIds((prev) => prev.includes(memberId) ? prev.filter((id) => id !== memberId) : [...prev, memberId]);

  const resetAddPanel = () => {
    setShowAdd(false);
    setEditId(null);
    setEditMember(null);
    setEditMemberFull(undefined);
    setEditMemberGender('');
    setEditOriginalFamilyCode('');
    setSelFamily(null);
    setEligible([]);
    setSelMemberIds([]);
    setAddOther(false); setOtherName(''); setOtherRelationship(OTHER_RELATIONSHIPS[0]); setOtherGender('');
    setGraveNumber(''); setBurialPlace(''); setDateOfDeath(''); setDateOfBurial(''); setNotes('');
    setSaveErr('');
  };

  const openEdit = async (row: CemeteryRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAdd(true);
    setEditId(row.cemetery_id);
    setEditMember(row.member_id ? { memberId: row.member_id, name: row.name } : null);
    setEditOriginalFamilyCode(row.family_code ?? '');
    setSelFamily({
      familyId:   row.family_id ?? 0,
      familyCode: row.family_code ?? '',
      familyHead: row.name,
    } as FamilyListItem);
    setEligible([]);
    setSelMemberIds(row.member_id ? [row.member_id] : []);
    setGraveNumber(row.grave_number ?? '');
    const rowAny = row as Record<string, unknown>;
    const burialPlaceVal = rowAny.burial_place ?? rowAny.place_of_burial ?? rowAny.BurialPlace ?? '';
    setBurialPlace(String(burialPlaceVal || ''));
    setDateOfDeath(row.date_of_death ? String(row.date_of_death).slice(0, 10) : '');
    setDateOfBurial(row.date_of_burial ? String(row.date_of_burial).slice(0, 10) : '');
    setNotes(row.notes ?? '');
    setSaveErr('');
    if (expandedId === row.cemetery_id) setExpandedId(null);

    // Load the linked member's full record so gender can be edited here
    // without wiping their other fields on save (see buildMemberPayloadPreserving).
    setEditMemberFull(undefined);
    setEditMemberGender('');
    if (row.member_id && row.family_id) {
      try {
        const members = await listFamilyMembers(row.family_id);
        const full = members.find((m) => Number(pickStr(m, ['memberId', 'member_id', 'MemberId', 'MemberID'])) === row.member_id);
        setEditMemberFull(full);
        setEditMemberGender(pickStr(full, ['gender', 'Gender']));
      } catch {
        // non-critical — gender edit just won't be available for this record
      }
    }
  };

  const submitNewRecord = async () => {
    const willAddOther = addOther && otherName.trim().length > 0;
    // Church (unlinked) records can skip formally identifying the deceased
    // person altogether — the family genuinely isn't known yet, so there's
    // no roster to add them to; whatever's worth noting goes in Notes
    // instead. A real family should still always have an actual member.
    const noMemberOk = !editId && selFamily?.familyCode === 'CHURCH';
    if (!selFamily || (!editId && selMemberIds.length === 0 && !willAddOther && !noMemberOk)) {
      setSaveErr('Please select a family and at least one member.');
      return;
    }
    if (addOther && !otherName.trim()) {
      setSaveErr('Please enter a name for the person not listed.');
      return;
    }
    if (!burialPlace.trim()) {
      setSaveErr('Burial place is required.');
      return;
    }
    setSaving(true);
    setSaveErr('');
    try {
      if (editId) {
        // Editing always targets the one member the record already belongs to.
        const currentGender = pickStr(editMemberFull, ['gender', 'Gender']);
        const genderChanged = !!editMemberGender && editMemberGender !== currentGender;
        // Linking: this record started on the CHURCH placeholder and a real
        // family has now been picked — move the member over first (this is
        // the family_member.family_id switch that makes linking possible;
        // sp_SaveFamilyMember's UPDATE already supports it unconditionally),
        // then sync cemetery_details.family_id to match below.
        const linking = editOriginalFamilyCode === 'CHURCH' && selFamily.familyCode !== 'CHURCH';
        // The CHURCH family's "Head" member is a shared stand-in, not a real
        // person — never move it. Doing so once dragged "Unidentified —
        // Church Records" into family AR20. The grave's link to the stand-in
        // is cleared server-side (cemetery/save.js); the person's name stays
        // in Notes like every other placeholder grave. Real people added via
        // "Other" never get the Head relationship, so they still move.
        const isStandIn = editOriginalFamilyCode === 'CHURCH'
          && /^head$/i.test(pickStr(editMemberFull, ['relationship', 'Relationship']));
        const moveMember = linking && !isStandIn;

        if (editMember?.memberId && !isStandIn && (genderChanged || moveMember)) {
          await updateFamilyMember(
            selFamily.familyId,
            editMember.memberId,
            buildMemberPayloadPreserving(editMemberFull, { gender: editMemberGender || currentGender }),
          );
        }

        await saveCemeteryRecord({
          cemeteryId:   editId,
          familyId:     selFamily.familyId,
          memberId:     editMember?.memberId ?? selMemberIds[0],
          graveNumber:  graveNumber || undefined,
          burialPlace:  burialPlace.trim(),
          dateOfDeath:  dateOfDeath || null,
          dateOfBurial: dateOfBurial || null,
          notes:        notes || undefined,
        });
      } else {
        // New record: one cemetery_details row per selected member, all
        // sharing the same grave number/burial place/dates/notes — this is
        // how several family members buried in the same grave get recorded
        // together instead of one at a time.
        const memberIds = [...selMemberIds];

        // Person buried before this system existed and never entered as a
        // family member — create a lightweight family_member record for
        // them first (same approach CertificateFormPage uses), then record
        // the cemetery entry against that new member.
        if (willAddOther) {
          const newMember = await createFamilyMember(selFamily.familyId, {
            name:         otherName.trim(),
            relationship: otherRelationship.trim() || 'Other',
            gender:       otherGender || undefined,
            status:       'Deceased',
          });
          const newMemberId = (newMember as { memberId?: number })?.memberId;
          if (newMemberId) memberIds.push(newMemberId);
        }

        if (memberIds.length === 0 && noMemberOk) {
          // No formal member — whoever this is only exists in Notes for now.
          await saveCemeteryRecord({
            familyId:     selFamily.familyId,
            graveNumber:  graveNumber || undefined,
            burialPlace:  burialPlace.trim(),
            dateOfDeath:  dateOfDeath || null,
            dateOfBurial: dateOfBurial || null,
            notes:        notes || undefined,
          });
        } else {
          for (const memberId of memberIds) {
            await saveCemeteryRecord({
              familyId:     selFamily.familyId,
              memberId,
              graveNumber:  graveNumber || undefined,
              burialPlace:  burialPlace.trim(),
              dateOfDeath:  dateOfDeath || null,
              dateOfBurial: dateOfBurial || null,
              notes:        notes || undefined,
            });
          }
        }
      }
      resetAddPanel();
      fetchData();
    } catch (err: any) {
      setSaveErr(err?.response?.data?.error ?? 'Failed to save cemetery record.');
    } finally {
      setSaving(false);
    }
  };

  // ── Subscription quick-edit logic ───────────────────────────
  // NOTE: cemetery subscriptions are tracked per-FAMILY per-year (not per
  // individual cemetery record), so we key off row.family_id.
  const toggleExpand = async (row: CemeteryRecord) => {
    if (expandedId === row.cemetery_id) { setExpandedId(null); return; }
    if (!row.family_id) return;
    setExpandedId(row.cemetery_id);
    setSubErr('');
    // Cemetery subscriptions are per-family (cemetery_subscription_yearly is
    // family_id NOT NULL) — nothing to load or bill until this grave is
    // linked to a real family.
    if (row.family_code === 'CHURCH') return;
    await loadSubscription(row.family_id, subYear);
  };

  const loadSubscription = async (familyId: number, year: number) => {
    setSubLoading(true);
    try {
      const data = await getCemeterySubscription(familyId, year);
      setSubAmount(String(data?.amount ?? ''));
      setSubDate(data?.payment_date ? String(data.payment_date).slice(0, 10) : '');
      setSubStatus((data?.payment_status as 'Paid' | 'Unpaid' | 'Pending') || 'Pending');
    } catch {
      setSubErr('Failed to load subscription.');
    } finally {
      setSubLoading(false);
    }
  };

  const changeSubYear = async (familyId: number, year: number) => {
    setSubYear(year);
    await loadSubscription(familyId, year);
  };

  const saveSubscription = async (familyId: number) => {
    setSubSaving(true);
    setSubErr('');
    try {
      await setCemeterySubscription(familyId, subYear, {
        amount:        parseFloat(subAmount) || 0,
        paymentDate:   subDate || null,
        paymentStatus: subStatus,
      });
    } catch (err: any) {
      setSubErr(err?.response?.data?.error ?? 'Failed to save subscription.');
    } finally {
      setSubSaving(false);
    }
  };

  const handleExport = async (format: ExportFormat, scope: ExportScope) => {
    const result = await listCemeteries({
      page: 1, pageSize: 9999,
      ...(scope === 'filtered' ? filterParams(filters) : {}),
      familyId: scope === 'filtered' ? (familyIdFilter ?? undefined) : undefined,
    });
    exportData({
      title:    'Parish Cemetery Records',
      filename: 'cemetery-export',
      columns: [
        { label: 'Name',           key: 'name'          },
        { label: 'Family Code',    key: 'family_code'   },
        { label: 'Gender',         key: 'gender'        },
        { label: 'Grave Number',   key: 'grave_number'  },
        { label: 'Date of Death',  key: 'date_of_death',  format: exportFmtDate },
        { label: 'Date of Burial', key: 'date_of_burial', format: exportFmtDate },
        { label: 'Notes',          key: 'notes'         },
      ],
      rows: result.items as unknown as Record<string, unknown>[],
      filters: {
        Search: filters.search ?? '',
        Family: filters.linked === 'true' ? 'Linked' : filters.linked === 'false' ? 'Not linked' : '',
        'Burial from': filters.burialFrom ?? '',
        'Burial to': filters.burialTo ?? '',
      },
    }, format);
  };

  const { sorted: sortedRows, sort, toggleSort } = useSort(rows as unknown as Record<string, unknown>[]);
  const displayRows = sortedRows as unknown as CemeteryRecord[];

  // How many records on the currently-loaded page share each grave number —
  // a lightweight, client-side hint (not a cross-page guarantee) so staff
  // can see at a glance when a grave already has more than one person on it.
  const graveCounts = useMemo(() => {
    const counts = new Map<string, number>();
    for (const r of rows) {
      const key = (r.grave_number ?? '').trim().toLowerCase();
      if (!key) continue;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    }
    return counts;
  }, [rows]);

  const confirmDelete = (row: CemeteryRecord, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTarget(row);
    setDeleteErr('');
    if (expandedId === row.cemetery_id) setExpandedId(null);
  };
  const cancelDelete = () => { setDeleteTarget(null); setDeleteErr(''); };
  const handleDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    setDeleteErr('');
    try {
      await deleteCemeteryRecord(deleteTarget.cemetery_id);
      setDeleteTarget(null);
      fetchData();
    } catch (err: any) {
      setDeleteErr(err?.response?.data?.error ?? 'Delete failed.');
    } finally {
      setDeleting(false);
    }
  };

  const handleFilterChange = (key: string, value: string | number | null) => {
    setFilters((prev) => ({ ...prev, [key]: value as string | null }));
    setPage(1);
  };
  const handleReset    = () => { setFilters(DEFAULT_FILTERS); setPage(1); };
  const handlePageSize = (s: number) => { setPageSize(s); setPage(1); };

  return (
    <div className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-semibold text-navy font-georgia">Cemetery</h1>
          <p className="text-xs text-teal mt-0.5">
            Parish cemetery records — click a row to manage its yearly subscription
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <ExportButton onExport={handleExport} hasFilters={!!(filters.search || filters.linked || filters.burialFrom || filters.burialTo || familyIdFilter)} />
          {isAdmin && !showAdd && (
            <button onClick={() => setShowAdd(true)} className="btn-primary">
              + Add Record
            </button>
          )}
        </div>
      </div>

      {showAdd && (
        <div className="bg-white border border-grid-border p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            <p className="font-georgia text-sm text-navy font-bold">{editId ? 'Edit Cemetery Record' : 'New Cemetery Record'}</p>
            <button onClick={resetAddPanel} className="text-xs text-teal hover:text-navy underline">Cancel</button>
          </div>

          {!selFamily ? (
            <div className="relative max-w-md">
              <label className="block text-xs text-teal uppercase tracking-wide mb-1">
                Search family by head name
              </label>
              <input
                type="text" value={famSearch}
                onChange={(e) => setFamSearch(e.target.value)}
                placeholder="Type a family head's name…"
                className="w-full border border-grid-border px-3 py-2 text-sm font-georgia focus:outline-none focus:border-teal"
              />
              {famSearching && <p className="text-xs text-teal mt-1">Searching…</p>}
              {famResults.length > 0 && (
                <ul className="mt-2 border border-grid-border divide-y divide-grid-border">
                  {famResults.map((f) => (
                    <li
                      key={f.familyId}
                      onClick={() => selectFamilyForAdd(f)}
                      className="px-3 py-2 text-sm font-georgia text-navy hover:bg-teal hover:text-white cursor-pointer"
                    >
                      {String(f.familyHead ?? f.familyCode)} — {f.familyCode}
                    </li>
                  ))}
                </ul>
              )}
              {!editId && (
                <button
                  type="button"
                  onClick={selectChurchPlaceholder}
                  disabled={placeholderLoading}
                  className="text-xs text-teal hover:text-navy underline mt-2 disabled:opacity-50"
                >
                  {placeholderLoading ? 'Loading…' : "Not linked to a family yet — record it anyway"}
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-georgia text-navy">
                  Family: <strong>{String(selFamily.familyHead ?? selFamily.familyCode)}</strong> ({selFamily.familyCode})
                  {selFamily.familyCode === 'CHURCH' && (
                    <span className="ml-1.5 text-xs font-normal text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded align-middle">
                      not linked
                    </span>
                  )}
                </p>
                {(!editId || editOriginalFamilyCode === 'CHURCH') && (
                  <button onClick={() => { setSelFamily(null); setEligible([]); setSelMemberIds([]); setAddOther(false); setOtherName(''); setOtherRelationship(OTHER_RELATIONSHIPS[0]); setOtherGender(''); }}
                          className="text-xs text-teal hover:text-navy underline">
                    {editId ? 'Link to a family' : 'Change family'}
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs text-teal uppercase tracking-wide mb-1">
                    {editId ? 'Member' : 'Member(s) sharing this grave'}
                  </label>
                  {editId ? (
                    <p className="w-full border border-grid-border bg-cream-light px-2 py-2 text-sm font-georgia text-navy">
                      {editMember?.name ?? '—'}
                    </p>
                  ) : null}
                  {editId && editMemberFull && (
                    <div className="mt-2">
                      <label className="block text-xs text-teal uppercase tracking-wide mb-1">Gender</label>
                      <select
                        value={editMemberGender}
                        onChange={(e) => setEditMemberGender(e.target.value)}
                        className="w-full border border-grid-border px-2 py-2 text-sm font-georgia text-navy"
                      >
                        <option value="">Gender —</option>
                        {OTHER_GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                  )}
                  {!editId && (
                    <>
                      <div className="w-full border border-grid-border px-2 py-2 max-h-40 overflow-y-auto space-y-1">
                        {eligible.map((m) => (
                          <label key={m.member_id} className="flex items-center gap-2 text-sm font-georgia text-navy cursor-pointer">
                            <input
                              type="checkbox"
                              checked={selMemberIds.includes(m.member_id)}
                              onChange={() => toggleSelMember(m.member_id)}
                              className="h-3.5 w-3.5 cursor-pointer"
                            />
                            {m.name} ({m.relationship})
                          </label>
                        ))}
                        {eligible.length === 0 && (
                          <p className="text-xs text-teal">No eligible members (all already recorded).</p>
                        )}
                        <label className="flex items-center gap-2 text-sm font-georgia text-navy cursor-pointer border-t border-grid-border pt-1 mt-1">
                          <input
                            type="checkbox"
                            checked={addOther}
                            onChange={(e) => setAddOther(e.target.checked)}
                            className="h-3.5 w-3.5 cursor-pointer"
                          />
                          Other (not in the list above)
                        </label>
                      </div>
                      {selFamily?.familyCode === 'CHURCH' && !addOther && selMemberIds.length === 0 && (
                        <p className="text-xs text-teal mt-1">
                          Don't know who this is yet? Leave this unchecked and just describe them in Notes below.
                        </p>
                      )}
                      {eligible.length > 0 && (
                        <p className="text-xs text-teal mt-1">
                          Select more than one to record them together on the same grave.
                        </p>
                      )}
                      {addOther && (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mt-2">
                          <div>
                            <input
                              type="text"
                              value={otherName}
                              onChange={(e) => setOtherName(e.target.value)}
                              placeholder="Name"
                              className="w-full border border-grid-border px-2 py-2 text-sm font-georgia text-navy"
                            />
                          </div>
                          <div>
                            <select
                              value={otherRelationship}
                              onChange={(e) => setOtherRelationship(e.target.value)}
                              className="w-full border border-grid-border px-2 py-2 text-sm font-georgia text-navy"
                            >
                              {OTHER_RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
                            </select>
                          </div>
                          <div>
                            <select
                              value={otherGender}
                              onChange={(e) => setOtherGender(e.target.value)}
                              className="w-full border border-grid-border px-2 py-2 text-sm font-georgia text-navy"
                            >
                              <option value="">Gender —</option>
                              {OTHER_GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                            </select>
                          </div>
                          <p className="sm:col-span-2 text-xs text-teal">
                            For someone not already on this family's member list — records them here as
                            deceased in one step, so their grave can be tracked.
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-teal uppercase tracking-wide mb-1">Grave Number</label>
                  <input type="text" value={graveNumber} onChange={(e) => setGraveNumber(e.target.value)}
                         className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
                </div>
                <div>
                  <label className="block text-xs text-teal uppercase tracking-wide mb-1">Burial Place *</label>
                  <input type="text" value={burialPlace} onChange={(e) => setBurialPlace(e.target.value)}
                         className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
                </div>
                <div>
                  <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date of Death</label>
                  <input type="date" value={dateOfDeath} onChange={(e) => setDateOfDeath(e.target.value)}
                         className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
                </div>
                <div>
                  <label className="block text-xs text-teal uppercase tracking-wide mb-1">Date of Burial</label>
                  <input type="date" value={dateOfBurial} onChange={(e) => setDateOfBurial(e.target.value)}
                         className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-xs text-teal uppercase tracking-wide mb-1">Notes</label>
                  <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2}
                            className="w-full border border-grid-border px-2 py-2 text-sm font-georgia focus:outline-none focus:border-teal" />
                </div>
              </div>

              {saveErr && <p className="text-sm text-red-600 font-georgia">{saveErr}</p>}

              <button onClick={submitNewRecord} disabled={saving} className="btn-primary">
                {saving
                  ? 'Saving…'
                  : !editId && selMemberIds.length > 1
                  ? `Save ${selMemberIds.length} Records`
                  : 'Save Record'}
              </button>
            </div>
          )}
        </div>
      )}

      <FilterBar fields={FILTER_FIELDS} values={filters} onChange={handleFilterChange} onReset={handleReset} />

      {familyIdFilter && (
        <div className="flex items-center justify-between bg-teal/10 border border-teal/30 text-sm font-georgia px-4 py-2 mb-3">
          <span>
            Showing cemetery records for family{' '}
            <strong>{familyCodeFilter || `#${familyIdFilter}`}</strong> only.
          </span>
          <button onClick={clearFamilyFilter} className="text-teal underline hover:no-underline">
            Show all records
          </button>
        </div>
      )}

      {fetchErr && <div className="text-sm text-red-600 font-georgia mb-4">{fetchErr}</div>}

      <div className="bg-white border border-grid-border overflow-x-auto">
        <div className="hidden sm:block">
        <table className="min-w-full text-sm font-georgia">
          <thead>
            <tr className="border-b border-grid-border bg-cream-light text-left text-xs text-teal uppercase tracking-wide">
              <SortableTh label="Name"           sortKey="name"          sort={sort} onSort={toggleSort} />
              <SortableTh label="Family Code"    sortKey="family_code"   sort={sort} onSort={toggleSort} />
              <SortableTh label="Gender"         sortKey="gender"        sort={sort} onSort={toggleSort} />
              <SortableTh label="Grave No."      sortKey="grave_number"  sort={sort} onSort={toggleSort} />
              <SortableTh label="Date of Death"  sortKey="date_of_death" sort={sort} onSort={toggleSort} />
              <SortableTh label="Date of Burial" sortKey="date_of_burial" sort={sort} onSort={toggleSort} />
              <th className="px-4 py-3" title="This year's subscription — shared by every grave under the same family">Sub. ({currentYear})</th>
              <th className="px-4 py-3">Notes</th>
              {isAdmin && <th className="px-4 py-3 text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={isAdmin ? 9 : 8} className="px-4 py-6 text-center text-teal">Loading…</td></tr>
            )}
            {!loading && displayRows.length === 0 && (
              <tr><td colSpan={isAdmin ? 9 : 8} className="px-4 py-6 text-center text-teal">No cemetery records found.</td></tr>
            )}
            {!loading && displayRows.map((r) => (
              <Fragment key={r.cemetery_id}>
                <tr
                  onClick={() => toggleExpand(r)}
                  className={`border-b border-grid-border last:border-0 cursor-pointer hover:bg-cream-light ${
                    expandedId === r.cemetery_id ? 'bg-cream-light' : ''
                  }`}
                >
                  <td className="px-4 py-2.5 text-navy font-bold">{displayName(r)}</td>
                  <td className="px-4 py-2.5 text-navy">{familyCodeLabel(r.family_code)}</td>
                  <td className="px-4 py-2.5 text-navy">{r.gender ?? '—'}</td>
                  <td className="px-4 py-2.5 text-navy">
                    {r.grave_number ?? '—'}
                    {r.grave_number && (graveCounts.get(r.grave_number.trim().toLowerCase()) ?? 0) > 1 && (
                      <span className="ml-1.5 text-xs font-normal text-teal bg-teal/10 px-1.5 py-0.5 rounded">
                        shared
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-navy">{fmtDate(r.date_of_death)}</td>
                  <td className="px-4 py-2.5 text-navy">{fmtDate(r.date_of_burial)}</td>
                  <td className="px-4 py-2.5"><SubStatusBadge status={r.subscription_status} /></td>
                  <td className="px-4 py-2.5 text-navy">{r.notes ?? '—'}</td>
                  {isAdmin && (
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <button
                        onClick={(e) => openEdit(r, e)}
                        className="text-teal hover:underline text-xs mr-3"
                      >
                        Edit
                      </button>
                      <button
                        onClick={(e) => confirmDelete(r, e)}
                        className="btn-danger !px-2 !py-1 text-xs"
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
                {expandedId === r.cemetery_id && (
                  <tr className="border-b border-grid-border last:border-0 bg-cream-light/50">
                    <td colSpan={isAdmin ? 9 : 8} className="px-4 py-4">
                      <div className="bg-white border border-grid-border p-4 max-w-xl">
                        {r.family_code === 'CHURCH' ? (
                          <div>
                            <p className="text-sm font-georgia text-navy">
                              This grave isn't linked to a family yet, so it has no subscription to manage.
                            </p>
                            {isAdmin && (
                              <button
                                onClick={(e) => openEdit(r, e)}
                                className="text-xs text-teal hover:text-navy underline mt-2"
                              >
                                Edit record to link a family
                              </button>
                            )}
                          </div>
                        ) : (
                          <>
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-3">
                              <p className="text-xs text-teal uppercase tracking-wide">Cemetery Subscription</p>
                              <select
                                value={subYear}
                                onChange={(e) => changeSubYear(r.family_id!, parseInt(e.target.value, 10))}
                                className="border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none focus:border-teal"
                              >
                                {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                              </select>
                            </div>

                            {subLoading ? (
                              <p className="text-sm text-teal">Loading…</p>
                            ) : (
                              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 items-end">
                                <div>
                                  <label className="block text-xs text-teal uppercase tracking-wide mb-1">Amount</label>
                                  <input
                                    type="number" step="0.01" value={subAmount} disabled={!isAdmin}
                                    onChange={(e) => setSubAmount(e.target.value)}
                                    className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-teal uppercase tracking-wide mb-1">Paid Date</label>
                                  <input
                                    type="date" value={subDate} disabled={!isAdmin}
                                    onChange={(e) => setSubDate(e.target.value)}
                                    className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs text-teal uppercase tracking-wide mb-1">Status</label>
                                  <select
                                    value={subStatus} disabled={!isAdmin}
                                    onChange={(e) => setSubStatus(e.target.value as 'Paid' | 'Unpaid' | 'Pending')}
                                    className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light"
                                  >
                                    <option value="Pending">Pending</option>
                                    <option value="Unpaid">Unpaid</option>
                                    <option value="Paid">Paid</option>
                                  </select>
                                </div>
                              </div>
                            )}

                            {subErr && <p className="text-sm text-red-600 font-georgia mt-2">{subErr}</p>}

                            {isAdmin && (
                              <button
                                onClick={() => saveSubscription(r.family_id!)}
                                disabled={subSaving || subLoading}
                                className="btn-primary text-xs px-3 py-1.5 mt-3"
                              >
                                {subSaving ? 'Saving…' : 'Save Subscription'}
                              </button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
              </Fragment>
            ))}
          </tbody>
        </table>
        </div>

        {/* Mobile: compact cards */}
        <div className="sm:hidden divide-y divide-grid-border">
          {loading && <p className="px-4 py-6 text-center text-teal text-sm">Loading…</p>}
          {!loading && displayRows.length === 0 && (
            <p className="px-4 py-6 text-center text-teal text-sm">No cemetery records found.</p>
          )}
          {!loading && displayRows.map((r) => {
            const isExpanded = expandedId === r.cemetery_id;
            const shared = r.grave_number && (graveCounts.get(r.grave_number.trim().toLowerCase()) ?? 0) > 1;
            return (
              <div key={r.cemetery_id}>
                <div
                  onClick={() => toggleExpand(r)}
                  className={`p-3 cursor-pointer ${isExpanded ? 'bg-cream-light' : 'active:bg-cream-light'}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-bold text-navy font-georgia truncate">{displayName(r)}</p>
                      <p className="text-xs text-teal font-georgia">{familyCodeLabel(r.family_code)}</p>
                    </div>
                    {shared && (
                      <span className="text-xs font-normal text-teal bg-teal/10 px-1.5 py-0.5 rounded flex-shrink-0">shared</span>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">{r.gender ?? '—'}</span>
                    <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">Grave {r.grave_number ?? '—'}</span>
                    <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">Died {fmtDate(r.date_of_death)}</span>
                    <span className="text-xs bg-offwhite text-navy rounded-full px-2 py-0.5 font-georgia">Buried {fmtDate(r.date_of_burial)}</span>
                  </div>
                  <div className="mt-1.5 flex items-center gap-1.5">
                    <span className="text-xs text-teal">Sub. ({currentYear}):</span>
                    <SubStatusBadge status={r.subscription_status} />
                  </div>
                  {r.notes && <p className="text-xs text-gray-500 font-georgia mt-1.5">{r.notes}</p>}
                  {isAdmin && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={(e) => openEdit(r, e)} className="flex-1 btn-secondary !py-1.5 text-xs">Edit</button>
                      <button onClick={(e) => confirmDelete(r, e)} className="flex-1 btn-danger !py-1.5 text-xs">Delete</button>
                    </div>
                  )}
                </div>
                {isExpanded && (
                  <div className="border-t border-grid-border bg-cream-light/50 p-3">
                    <div className="bg-white border border-grid-border p-3">
                      {r.family_code === 'CHURCH' ? (
                        <div>
                          <p className="text-sm font-georgia text-navy">
                            This grave isn't linked to a family yet, so it has no subscription to manage.
                          </p>
                          {isAdmin && (
                            <button
                              onClick={(e) => openEdit(r, e)}
                              className="text-xs text-teal hover:text-navy underline mt-2"
                            >
                              Edit record to link a family
                            </button>
                          )}
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between gap-2 mb-3">
                            <p className="text-xs text-teal uppercase tracking-wide">Cemetery Subscription</p>
                            <select
                              value={subYear}
                              onChange={(e) => changeSubYear(r.family_id!, parseInt(e.target.value, 10))}
                              className="border border-grid-border px-2 py-1 text-sm font-georgia focus:outline-none focus:border-teal"
                            >
                              {YEAR_OPTIONS.map((y) => <option key={y} value={y}>{y}</option>)}
                            </select>
                          </div>
                          {subLoading ? (
                            <p className="text-sm text-teal">Loading…</p>
                          ) : (
                            <div className="grid grid-cols-1 gap-3">
                              <div>
                                <label className="block text-xs text-teal uppercase tracking-wide mb-1">Amount</label>
                                <input
                                  type="number" step="0.01" value={subAmount} disabled={!isAdmin}
                                  onChange={(e) => setSubAmount(e.target.value)}
                                  className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-teal uppercase tracking-wide mb-1">Paid Date</label>
                                <input
                                  type="date" value={subDate} disabled={!isAdmin}
                                  onChange={(e) => setSubDate(e.target.value)}
                                  className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light"
                                />
                              </div>
                              <div>
                                <label className="block text-xs text-teal uppercase tracking-wide mb-1">Status</label>
                                <select
                                  value={subStatus} disabled={!isAdmin}
                                  onChange={(e) => setSubStatus(e.target.value as 'Paid' | 'Unpaid' | 'Pending')}
                                  className="w-full border border-grid-border px-2 py-1.5 text-sm font-georgia focus:outline-none focus:border-teal disabled:bg-cream-light"
                                >
                                  <option value="Pending">Pending</option>
                                  <option value="Unpaid">Unpaid</option>
                                  <option value="Paid">Paid</option>
                                </select>
                              </div>
                            </div>
                          )}
                          {subErr && <p className="text-sm text-red-600 font-georgia mt-2">{subErr}</p>}
                          {isAdmin && (
                            <button
                              onClick={() => saveSubscription(r.family_id!)}
                              disabled={subSaving || subLoading}
                              className="btn-primary text-xs px-3 py-1.5 mt-3 w-full"
                            >
                              {subSaving ? 'Saving…' : 'Save Subscription'}
                            </button>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <PaginationBar
        page={page} pageSize={pageSize} total={total} totalPages={totalPages}
        onPage={setPage} onPageSize={handlePageSize}
      />

      {deleteTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-sm max-h-[90vh] overflow-y-auto p-6">
            <h2 className="text-base font-semibold text-navy font-georgia mb-2">Delete Cemetery Record</h2>
            <p className="text-sm text-gray-600 mb-4">
              Delete record for <span className="font-semibold">{deleteTarget.name}</span>? This cannot be undone.
            </p>
            {deleteErr && <p className="text-red-600 text-sm mb-3">{deleteErr}</p>}
            <div className="flex justify-end gap-3">
              <button onClick={cancelDelete} className="btn-secondary" disabled={deleting}>Cancel</button>
              <button onClick={handleDelete} className="btn-danger" disabled={deleting}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
